import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebaseConfig';

// Initialize Firebase Functions with explicit region
const functions = getFunctions(app, 'us-central1');

// Define callable function types
type EmailResponse = {
  success: boolean;
  message: string;
};

/**
 * Email Service for Baakas Buyer Web App
 * 
 * This service provides helper functions to trigger email notifications
 * by calling Firebase Cloud Functions that integrate with Brevo email service.
 */

/**
 * Trigger order confirmation email to buyer
 * @param email - Buyer's email address
 * @param orderId - Order ID for the confirmation
 * @returns Promise with success status and message
 */
export async function triggerBuyerOrderEmail(
  email: string, 
  orderId: string
): Promise<EmailResponse> {
  try {
    // Removed debug log to prevent sensitive data exposure (email)
    
    // Validate input parameters
    if (!email || !orderId) {
      throw new Error('Email and orderId are required parameters');
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Invalid email format');
    }

    // Use httpsCallable to avoid CORS issues
    const sendBuyerOrderEmailFn = httpsCallable(functions, 'sendBuyerOrderEmailFn');
    // Removed debug logs to prevent sensitive data exposure
    
    const result = await sendBuyerOrderEmailFn({ email, orderId });
    // Removed debug log to prevent sensitive data exposure
    
    return result.data as EmailResponse;
  } catch (error) {
    // SECURITY: Removed console.error to prevent information leakage
    
    // Handle different types of errors
    if (error instanceof Error) {
      return {
        success: false,
        message: `Failed to send order confirmation email: ${error.message}`
      };
    }
    
    return {
      success: false,
      message: 'Failed to send order confirmation email: Unknown error occurred'
    };
  }
}

/**
 * Trigger delivery notification email to buyer
 * @param email - Buyer's email address
 * @param orderId - Order ID for the delivery notification
 * @returns Promise with success status and message
 */
export async function triggerBuyerDeliveryEmail(
  email: string, 
  orderId: string
): Promise<EmailResponse> {
  try {
    // Removed debug log to prevent sensitive data exposure (email)
    
    // Validate input parameters
    if (!email || !orderId) {
      throw new Error('Email and orderId are required parameters');
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Invalid email format');
    }

    // Use httpsCallable to avoid CORS issues
    try {
      const sendBuyerDeliveryEmailFn = httpsCallable(functions, 'sendBuyerDeliveryEmailFn');
      // Removed debug logs to prevent sensitive data exposure
      
      const result = await sendBuyerDeliveryEmailFn({ email, orderId });
      // Removed debug log to prevent sensitive data exposure
      
      return result.data as EmailResponse;
    } catch (fetchError) {
      // If the function is not available (quota issues), return a success response
      // to prevent blocking the order tracking process
      // SECURITY: Removed console.warn to prevent information leakage
      return {
        success: true,
        message: 'Delivery notification temporarily unavailable - order still processed successfully'
      };
    }
  } catch (error) {
    // SECURITY: Removed console.error to prevent information leakage
    
    // Handle different types of errors
    if (error instanceof Error) {
      return {
        success: false,
        message: `Failed to send delivery notification email: ${error.message}`
      };
    }
    
    return {
      success: false,
      message: 'Failed to send delivery notification email: Unknown error occurred'
    };
  }
}

/**
 * Trigger new order notification email to seller
 * @param email - Seller's email address
 * @param orderId - Order ID for the notification
 * @param productName - Name of the product ordered
 * @returns Promise with success status and message
 */
export async function triggerSellerNewOrderEmail(
  email: string, 
  orderId: string,
  productName: string
): Promise<EmailResponse> {
  try {
    // Removed debug log to prevent sensitive data exposure (email)
    
    // Validate input parameters
    if (!email || !orderId || !productName) {
      throw new Error('Email, orderId, and productName are required parameters');
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Invalid email format');
    }

    // Use httpsCallable to avoid CORS issues
    try {
      const sendSellerNewOrderEmailFn = httpsCallable(functions, 'sendSellerNewOrderEmailFn');
      // Removed debug logs to prevent sensitive data exposure
      
      const result = await sendSellerNewOrderEmailFn({ email, orderId, productName });
      // Removed debug log to prevent sensitive data exposure
      
      return result.data as EmailResponse;
    } catch (fetchError) {
      // If the function is not available (quota issues), return a success response
      // to prevent blocking the checkout process
      // SECURITY: Removed console.warn to prevent information leakage
      return {
        success: true,
        message: 'Seller notification temporarily unavailable - order still processed successfully'
      };
    }
  } catch (error) {
    // SECURITY: Removed console.error to prevent information leakage
    
    // Handle different types of errors
    if (error instanceof Error) {
      return {
        success: false,
        message: `Failed to send seller new order notification email: ${error.message}`
      };
    }
    
    return {
      success: false,
      message: 'Failed to send seller new order notification email: Unknown error occurred'
    };
  }
}

/**
 * Trigger payout notification email to seller
 * @param email - Seller's email address
 * @param sellerName - Name of the seller
 * @param amount - Payout amount
 * @returns Promise with success status and message
 */
export async function triggerSellerPayoutEmail(
  email: string, 
  sellerName: string,
  amount: number
): Promise<EmailResponse> {
  try {
    // Removed debug log to prevent sensitive data exposure (email)
    
    // Validate input parameters
    if (!email || !sellerName || !amount) {
      throw new Error('Email, sellerName, and amount are required parameters');
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Invalid email format');
    }

    // Use httpsCallable to avoid CORS issues
    const sendSellerPayoutEmailFn = httpsCallable(functions, 'sendSellerPayoutEmailFn');
    // Removed debug logs to prevent sensitive data exposure
    
    const result = await sendSellerPayoutEmailFn({ email, sellerName, amount });
    // Removed debug log to prevent sensitive data exposure
    
    return result.data as EmailResponse;
  } catch (error) {
    // SECURITY: Removed console.error to prevent information leakage
    
    // Handle different types of errors
    if (error instanceof Error) {
      return {
        success: false,
        message: `Failed to send seller payout notification email: ${error.message}`
      };
    }
    
    return {
      success: false,
      message: 'Failed to send seller payout notification email: Unknown error occurred'
    };
  }
}

/**
 * Utility function to check if email service is available
 * @returns Promise<boolean> - true if service is available
 */
export async function isEmailServiceAvailable(): Promise<boolean> {
  try {
    // Try to get the functions instance
    const functionsInstance = getFunctions(app);
    return !!functionsInstance;
  } catch (error) {
    // SECURITY: Removed console.error to prevent information leakage
    return false;
  }
}

/**
 * Batch email sending for multiple orders
 * @param emails - Array of email objects with email and orderId
 * @returns Promise with results for each email
 */
export async function triggerBatchOrderEmails(
  emails: Array<{ email: string; orderId: string }>
): Promise<Array<EmailResponse & { email: string; orderId: string }>> {
  const results: Array<EmailResponse & { email: string; orderId: string }> = [];
  
  // Process emails in parallel for better performance
  const promises = emails.map(async ({ email, orderId }) => {
    const result = await triggerBuyerOrderEmail(email, orderId);
    return {
      ...result,
      email,
      orderId
    };
  });

  try {
    const batchResults = await Promise.allSettled(promises);
    
    batchResults.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        results.push(result.value);
      } else {
        results.push({
          success: false,
          message: `Failed to send email: ${result.reason}`,
          email: emails[index].email,
          orderId: emails[index].orderId
        });
      }
    });
  } catch (error) {
    // SECURITY: Removed console.error to prevent information leakage
  }

  return results;
}
